import { DrawerSection } from '@components/ui/drawer/section';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@components/ui/radio';
import { Button } from '@components/ui/button';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import {
  getCrewMembersFromForeman,
  mapCrewMembersToSchedulerSidebarCrewMembers,
} from '@contexts/scheduler/sidebar/helpers';
import { SchedulerSidebarConfirmOption } from '../option';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useEffect } from 'react';

export const SchedulerSidebarConfirmCrewChanged = () => {
  const {
    currentSchedule,
    defaultCrewChanged,
    confirm: {
      crewChanged: { open, keep },
    },
    crewMembers,
    setState,
  } = useSchedulerSidebar();

  const {
    active: { schedule },
  } = useSchedulerContext();

  const { fieldUsers } = useDropdownsContext();

  useEffect(() => {
    if (!currentSchedule || !schedule) return;
    if (schedule.id === 'new') return;
    if (!defaultCrewChanged) return;
    const differentForeman = currentSchedule?.foreman?.id !== schedule?.foreman?.id;

    setState((prev) => ({
      ...prev,
      confirm: {
        ...prev.confirm,
        crewChanged: { ...prev.confirm.crewChanged, open: !differentForeman },
      },
    }));
  }, [currentSchedule, schedule]);

  const { t } = useTranslation('app', { keyPrefix: 'schedule.confirm.crewChanged' });
  const { t: tActions } = useTranslation('app', { keyPrefix: 'actions' });

  const handleConfirm = () => {
    const defaultCrewMembers = getCrewMembersFromForeman(schedule.foreman?.id, fieldUsers);

    setState((prev) => ({
      ...prev,
      defaultCrewChanged: false,
      defaultCrew: !prev.confirm.crewChanged.keep,
      confirm: {
        ...prev.confirm,
        crewChanged: { ...prev.confirm.crewChanged, open: false },
      },
      crewMembers: prev.confirm.crewChanged.keep
        ? prev.crewMembers
        : mapCrewMembersToSchedulerSidebarCrewMembers(defaultCrewMembers),
    }));
  };

  const assignedFieldUser = fieldUsers.options.find((el) => el.id === schedule.foreman?.id) || null;

  const currentNames = crewMembers.map((el) => el.name);
  const defaultNames = mapCrewMembersToSchedulerSidebarCrewMembers(
    getCrewMembersFromForeman(schedule?.foreman?.id, fieldUsers),
  ).map((el) => el.name);

  if (!open) return null;
  return (
    <DrawerSection title={t('title')} alwaysOpen>
      <div className="flex flex-col gap-4">
        <p className="text-sm font-normal text-neutral-500">
          {t('message', { fieldUser: assignedFieldUser?.fullName || 'foreman' })}
        </p>
        <RadioGroup
          value={keep ? 'keep' : 'refresh'}
          onValueChange={(v) => {
            setState((prev) => ({
              ...prev,
              confirm: {
                ...prev.confirm,
                crewChanged: { ...prev.confirm.crewChanged, keep: v === 'keep' },
              },
            }));
          }}
          className="flex flex-col gap-2">
          <SchedulerSidebarConfirmOption
            id="crew-keep"
            value="keep"
            label={t('keep')}
            items={currentNames}
            emptyLabel={t('empty')}
          />
          <SchedulerSidebarConfirmOption
            id="crew-refresh"
            value="refresh"
            label={t('refresh', { fieldUser: assignedFieldUser?.fullName || '' })}
            items={defaultNames}
            emptyLabel={t('empty')}
          />
        </RadioGroup>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            type="button"
            size="sm"
            onClick={() => {
              setState((prev) => ({
                ...prev,
                defaultCrewChanged: false,
                defaultCrew: false,
                confirm: {
                  ...prev.confirm,
                  crewChanged: { ...prev.confirm.crewChanged, keep: true, open: false },
                },
              }));
            }}>
            {tActions('cancel')}
          </Button>
          <Button type="button" size="sm" onClick={handleConfirm}>
            {tActions('confirm')}
          </Button>
        </div>
      </div>
    </DrawerSection>
  );
};
